import { APP_API_URL } from "@/consts";
import graphqlClient from "@/GQLClient";
import type { LoggedUser } from "@/graphql";
import {
  DeleteUserDocument,
  GetByEmailOrUsernameDocument,
  GetSelfQueryDocument,
  GetUserStatisticsDocument,
  SaveUserDocument,
  type UserSaveInput,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";

const getToken = async (jwt: string) => {
  const response = await window.fetch(`${APP_API_URL}/user/accesstoken`, {
    method: "GET",
    headers: {
      Authorization: jwt,
    },
  });

  if (response.status === 200) {
    return await response.json();
  }

  throw new Error(`Auth token error.`);
};

const getSelf = async (): Promise<LoggedUser> => {
  const response = await graphqlClient.query({
    query: GetSelfQueryDocument,
    variables: {},
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.self) {
    return response.data.self;
  }

  throw new SentryGraphQLError(`api.user.getSelf`, response);
};

const save = async (user: UserSaveInput) => {
  const response = await graphqlClient.query({
    query: SaveUserDocument,
    variables: {
      user,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.user?.saveUser?.success) {
    return response.data.user.saveUser.id;
  }

  throw new SentryGraphQLError(`api.user.save`, response);
};

const deleteItem = async (id: string) => {
  const response = await graphqlClient.query({
    query: DeleteUserDocument,
    variables: {
      id,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.user?.deleteUser?.success) {
    return response.data.user.deleteUser.success;
  }

  throw new SentryGraphQLError(`api.user.deleteItem`, response);
};

const getUsersStatistics = async (ids: string[]) => {
  const response = await graphqlClient.query({
    query: GetUserStatisticsDocument,
    variables: {
      ids,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.user?.get) {
    return response.data.user.get;
  }

  throw new SentryGraphQLError(`api.user.getUsersStatistics`, response);
};

const getUserStatistics = async (id: string) => {
  const response = await getUsersStatistics([id]);
  const [user] = response;
  if (user) {
    return user;
  }

  throw new Error("GetUserStatistics query error.");
};

const getIdByEmailInUse = async (email: string) => {
  const response = await graphqlClient.query({
    query: GetByEmailOrUsernameDocument,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
    variables: {
      email,
      username: email,
    },
  });

  return response.data.user?.getByEmailOrUsername?.id;
};

export default {
  getToken,
  getSelf,
  save,
  deleteItem,
  getUsersStatistics,
  getUserStatistics,
  getIdByEmailInUse,
};
